import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Link2, Copy, ExternalLink, Filter, Search, Key, Plus, Trash2, Eye, EyeOff } from 'lucide-react';

// Use environment variable if available, otherwise use production backend URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || `${window.location.origin}/s`;
const API = `${BACKEND_URL}/api`;

const ShortList = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [shortUrls, setShortUrls] = useState([]);
  const [filteredUrls, setFilteredUrls] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [showGeneratedKey, setShowGeneratedKey] = useState(false);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!authLoading && !isAuthenticated) {
      toast.error('Please login to access Short List');
      navigate('/login');
      return;
    }

    // Check if user is Admin
    if (!authLoading && isAuthenticated && user) {
      if (user.role !== 'Admin') {
        toast.error('Access denied. This page is only accessible to Admins.');
        navigate('/');
        return;
      }

      // If user is Admin, fetch data
      fetchShortUrls();
      fetchClients();
      fetchAPIKeys();
    }
  }, [isAuthenticated, authLoading, user, navigate]);

  useEffect(() => {
    filterUrls();
  }, [shortUrls, selectedClient, searchQuery]);

  const fetchShortUrls = async () => {
    try {
      const response = await axios.get(`${API}/short-urls`);
      setShortUrls(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching short URLs:', error);
      toast.error('Failed to fetch short URLs');
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/short-urls/clients`);
      setClients(response.data.clients);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const filterUrls = () => {
    let filtered = [...shortUrls];

    // Filter by client
    if (selectedClient !== 'all') {
      filtered = filtered.filter(url => url.client_domain === selectedClient);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(url =>
        url.original_url.toLowerCase().includes(searchQuery.toLowerCase()) ||
        url.short_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        url.client_domain.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredUrls(filtered);
  };

  const fetchAPIKeys = async () => {
    try {
      const response = await axios.get(`${API}/api-keys`);
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error('Failed to fetch API keys');
    }
  };

  const createAPIKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    try {
      const response = await axios.post(`${API}/api-keys`, { name: newKeyName });
      setGeneratedKey(response.data);
      setShowGeneratedKey(true);
      setApiKeys([response.data, ...apiKeys]);
      setNewKeyName('');
      toast.success('API key created successfully!');
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error('Failed to create API key');
    }
  };

  const revokeAPIKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/api-keys/${keyId}`);
      setApiKeys(apiKeys.map(key => key.id === keyId ? { ...key, is_active: false } : key));
      toast.success('API key revoked successfully');
    } catch (error) {
      console.error('Error revoking API key:', error);
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Short URLs</h1>
          <p className="text-gray-600">Manage and track all your shortened URLs</p>
        </div>

        {/* API Keys Management */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Key className="mr-2 h-5 w-5 text-orange-500" />
                <CardTitle>API Keys</CardTitle>
              </div>
              <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-600">
                    <Plus className="mr-2 h-4 w-4" />
                    Generate API Key
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Generate New API Key</DialogTitle>
                  </DialogHeader>
                  
                  {!generatedKey ? (
                    <>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="keyName">API Key Name</Label>
                          <Input
                            id="keyName"
                            placeholder="e.g., Production API, Test Environment"
                            value={newKeyName}
                            onChange={(e) => setNewKeyName(e.target.value)}
                          />
                        </div>
                        <p className="text-sm text-gray-600">
                          Give your API key a descriptive name to help you identify it later.
                        </p>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowKeyDialog(false)}>
                          Cancel
                        </Button>
                        <Button className="bg-orange-500 hover:bg-orange-600" onClick={createAPIKey}>
                          Generate Key
                        </Button>
                      </DialogFooter>
                    </>
                  ) : (
                    <>
                      <div className="space-y-4 py-4">
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <p className="text-sm font-medium text-orange-900 mb-2">⚠️ Important: Save this key now!</p>
                          <p className="text-xs text-orange-700 mb-3">
                            This is the only time you'll see this key. Store it securely.
                          </p>
                          <div className="bg-white border border-gray-300 rounded p-3 font-mono text-sm break-all relative">
                            {showGeneratedKey ? generatedKey.key : '•'.repeat(50)}
                            <div className="absolute right-2 top-2 flex space-x-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setShowGeneratedKey(!showGeneratedKey)}
                                className="h-7 w-7 p-0"
                              >
                                {showGeneratedKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => copyToClipboard(generatedKey.key)}
                                className="h-7 w-7 p-0"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          onClick={() => {
                            setShowKeyDialog(false);
                            setGeneratedKey(null);
                            setShowGeneratedKey(false);
                          }}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Done
                        </Button>
                      </DialogFooter>
                    </>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {apiKeys.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Key className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                <p>No API keys yet. Generate one to start using the URL shortener API.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <p className="font-medium">{key.name}</p>
                        <Badge variant={key.is_active ? 'default' : 'secondary'}>
                          {key.is_active ? 'Active' : 'Revoked'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 font-mono mt-1">{key.key_preview}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Created {formatDate(key.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {key.is_active && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => revokeAPIKey(key.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900 font-medium mb-1">Using the API</p>
              <p className="text-xs text-blue-700">
                Include your API key in the <code className="bg-blue-100 px-1 py-0.5 rounded">X-API-Key</code> header when making requests to the shortener API.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by URL, short code, or domain..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Client Filter */}
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client} value={client}>
                        {client}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Total URLs</p>
                <p className="text-3xl font-bold text-orange-500">{shortUrls.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Filtered URLs</p>
                <p className="text-3xl font-bold text-blue-500">{filteredUrls.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Total Clicks</p>
                <p className="text-3xl font-bold text-green-500">
                  {shortUrls.reduce((sum, url) => sum + (url.click_count || 0), 0)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Link2 className="mr-2 h-5 w-5" />
              Short URLs List
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading short URLs...</p>
              </div>
            ) : filteredUrls.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No short URLs found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Short Code</TableHead>
                      <TableHead>Original URL</TableHead>
                      <TableHead>Client Domain</TableHead>
                      <TableHead>Clicks</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUrls.map((url) => (
                      <TableRow key={url.id}>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                              {url.short_code}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(url.short_url)}
                              className="h-6 w-6 p-0"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-md truncate text-sm" title={url.original_url}>
                            {url.original_url}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {url.client_domain}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            {url.click_count || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatDate(url.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(url.short_url)}
                              title="Copy short URL"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              asChild
                              title="Visit URL"
                            >
                              <a href={url.original_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ShortList;
